import { useState } from 'react'

import { Box, Button, Checkbox, Text } from '@factorialco/deprecated-design-system'
import type { Form } from 'factorial-form'
import { Features } from 'generated/featureDefinitions'
import {
  ProjectForSelectFragment,
  ProjectManagementExportModalDocument,
} from 'generated/resources/graphql'
import compact from 'lodash/compact'
import moment from 'moment'

import { observer } from 'mobx-react'

import { EmployeeForSelectFragmentNoAccess } from 'modules/core/components/EntitySelectWrapper/Entities/Employees/types'
import DayPicker from 'modules/core/components/Inputs/DayPicker'
import Select from 'modules/core/components/Inputs/Select'
import InputText from 'modules/core/components/Inputs/Text'
import Modal from 'modules/core/components/Modal'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { EmployeeSelector } from 'modules/employees/components/EmployeeSelector'
import { ProjectSelector } from 'modules/project_management/components/selectors/ProjectSelector'
import useProjectsExport from 'modules/project_management/hooks/useProjectsExport'
import {
  dateRangeOptions,
  exportTypeOptions,
  fileFormatOptions,
  timeFormatOptions,
} from 'modules/project_management/lib/exportFormOptions'

import { FormValues } from './types'

export interface ExportProjectsModalProps {
  onClose: () => void
  canSeeCost: boolean
  exportForm: Form
  project?: ProjectForSelectFragment
  hasTimeTrackingSection: boolean
  hasFlexibleTracking: boolean
}

const ExportProjectsModal = ({
  onClose,
  canSeeCost,
  exportForm,
  project,
  hasTimeTrackingSection,
  hasFlexibleTracking,
}: ExportProjectsModalProps) => {
  const [selectedProjects, setSelectedProjects] = useState<ProjectForSelectFragment[]>(
    compact([project])
  )
  const [selectedEmployees, setSelectedEmployees] = useState<EmployeeForSelectFragmentNoAccess[]>(
    []
  )
  const [includeEmployeesWithoutTimeTracked, setIncludeEmployeesWithoutTimeTracked] =
    useState<boolean>(false)

  const [includeAllProjects, setIncludeAllProjects] = useState<boolean>(false)

  const { company } = useCurrentSession()

  const { data: policyData } = useQuery(ProjectManagementExportModalDocument, {
    variables: {
      companyId: company.id,
      featureIds: [Features.projects_clients, Features.projects_with_subprojects],
    },
  })

  const hasClientsFeature = !!policyData?.companies.featureAvailabilitiesConnection.nodes.find(
    (feature) => feature.id === Features.projects_clients
  )?.available

  const hasSubprojectsFeature = !!policyData?.companies.featureAvailabilitiesConnection.nodes.find(
    (feature) => feature.id === Features.projects_with_subprojects
  )?.available

  const canManageProjectTasks =
    policyData?.projectManagement._policies.projectTaskManage.can ?? false

  const exportTimeAndCostByMonthsFFenabled =
    policyData?.features.featuresConnection.nodes[0]?.enabled ?? false

  const handleEmployeeChange = (newEmployees: EmployeeForSelectFragmentNoAccess[]) => {
    setSelectedEmployees(newEmployees)
    exportForm.get('employee_ids').set(newEmployees.map((employee) => employee.id))
  }

  const onProjectChange = (projects: ProjectForSelectFragment[]) => {
    setSelectedProjects(projects)
    exportForm.get('project_ids').set(projects.map((p) => p.id))
  }

  const onIncludeEmployeesWithoutTimeTrackedChange = (value: boolean) => {
    setIncludeEmployeesWithoutTimeTracked(value)
    exportForm.get('include_employees_without_time_tracked').set(value)
  }

  const onAllProjectsChange = (value: boolean) => {
    setIncludeAllProjects(value)
    exportForm.get('all_projects').set(value)

    if (value === true) {
      onProjectChange([])
    }
  }

  const {
    name,
    export_type,
    file_format,
    time_format,
    project_ids,
    employee_ids,
    date_range,
    from_date,
    to_date,
  } = exportForm.data()

  const { onExport } = useProjectsExport({
    formValues: exportForm.values,
    hasClientsFeature,
    hasSubprojectsFeature,
  })

  const fromMaxDate = () => {
    return !exportForm.get('to_date').value
      ? moment().endOf('day')
      : moment(stringToDate(exportForm.get('to_date').value, 'ui')).endOf('day')
  }

  const mandatoryFieldsCompleted = () => {
    let mandatory = false
    let optional = true

    if (isShiftsType() || isEmployeeType()) {
      mandatory = !!employee_ids.length
    } else {
      mandatory = !!project_ids.length || includeAllProjects
    }

    if (date_range === 'custom_range') {
      optional = !!from_date && !!to_date
    }
    if (file_format === 'xlsx' && !isExpensesType()) {
      optional = optional && !!time_format
    }

    return !!name && mandatory && optional
  }

  if (file_format !== 'xlsx') exportForm.get('time_format').value = ''
  if (date_range !== 'custom_range') {
    exportForm.get('from_date').value = ''
    exportForm.get('to_date').value = ''
  }

  const trackAmplitude = (formValues: FormValues) => {
    track(trackingEvents.projectManagement.projects.downloadProjectsReportSubmit, {
      export_type: exportTypeOptions().find((element) => element.value === formValues.export_type)
        ?.value,
      num_projects_selected: formValues.project_ids?.length,
      num_employees_selected: formValues.employee_ids?.length,
      data_range: formValues.date_range,
      format_option: formValues.file_format,
      time_format: formValues.time_format === 'hours' ? 'decimals' : formValues.time_format,
      include_employees_without_time_tracked: formValues.include_employees_without_time_tracked,
      all_projects: formValues.all_projects,
    })
  }

  const submitModal = () => {
    onExport()
    trackAmplitude(exportForm.values)
    onClose()
  }

  const isTotalTimeType = () => {
    return export_type === 'total_time'
  }

  const isShiftsType = () => {
    return export_type === 'shifts'
  }

  const isEmployeeType = () => {
    return export_type === 'total_time_by_employee'
  }

  const isExpensesType = () => {
    return export_type === 'expenses'
  }

  const filteredExportTypeOptions = () => {
    const options = []

    if (hasTimeTrackingSection || hasFlexibleTracking) {
      const totalTimeOption = exportTypeOptions().find((element) => element.value === 'total_time')
      const totalTimeByDayOption = exportTypeOptions().find(
        (element) => element.value === 'total_time_by_date'
      )
      const totalTimeAndCostByMonthOption = exportTypeOptions().find(
        (element) => element.value === 'total_time_and_cost_by_month'
      )
      const employeesOption = exportTypeOptions().find(
        (element) => element.value === 'total_time_by_employee'
      )
      const shiftsOption = exportTypeOptions().find((element) => element.value === 'shifts')
      if (totalTimeOption !== undefined) options.push(totalTimeOption)
      if (totalTimeByDayOption !== undefined) options.push(totalTimeByDayOption)
      if (
        exportTimeAndCostByMonthsFFenabled &&
        canSeeCost &&
        totalTimeAndCostByMonthOption !== undefined
      )
        options.push(totalTimeAndCostByMonthOption)
      if (shiftsOption !== undefined) options.push(shiftsOption)
      if (employeesOption !== undefined) options.push(employeesOption)
    }

    const expensesOption = exportTypeOptions().find((element) => element.value === 'expenses')
    if (expensesOption !== undefined && canSeeCost) options.push(expensesOption)

    if (canManageProjectTasks) {
      const tasksOption = exportTypeOptions().find((element) => element.value === 'tasks')
      if (tasksOption !== undefined) options.push(tasksOption)
    }

    return options
  }

  return (
    <Modal
      illustration={'document-edit'}
      title={i18n.t('project_management.modals.export_projects_data.title')}
      description={i18n.t('project_management.modals.export_projects_data.description')}
      onClose={onClose}
      onSubmit={submitModal}
      footer={
        <>
          <Button type="secondary" onClick={onClose}>
            {i18n.t('common.cancel')}
          </Button>
          <Button disabled={!mandatoryFieldsCompleted()} submit>
            {i18n.t('project_management.modals.export_projects_data.submit')}
          </Button>
        </>
      }
    >
      <Box gap="s16" width="s400">
        <InputText
          label={i18n.t('project_management.modals.export_projects_data.input_label.file_name')}
          field={exportForm.get('name')}
          mandatory
        />
        <Select
          label={i18n.t('project_management.modals.export_projects_data.input_label.export_type')}
          options={filteredExportTypeOptions()}
          field={exportForm.get('export_type')}
          readonly={filteredExportTypeOptions().length === 1}
        />
        <Select
          label={i18n.t('project_management.modals.export_projects_data.input_label.file_format')}
          options={fileFormatOptions}
          field={exportForm.get('file_format')}
        />
        {file_format === 'xlsx' && !isExpensesType() && (
          <Select
            label={i18n.t('project_management.modals.export_projects_data.input_label.time_format')}
            options={timeFormatOptions()}
            field={exportForm.get('time_format')}
            none={i18n.t('common.select')}
          />
        )}
        {!isShiftsType() && !isEmployeeType() && (
          <>
            <ProjectSelector
              multiple
              mandatory={!includeAllProjects}
              disabled={!!project || includeAllProjects}
              value={selectedProjects}
              onChange={onProjectChange}
            />
            {!project && (
              <Checkbox
                checked={includeAllProjects}
                label={i18n.t(
                  'project_management.modals.export_projects_data.input_label.all_projects'
                )}
                size="small"
                onChange={(event) => onAllProjectsChange(event.target.checked)}
              />
            )}
          </>
        )}
        {(isShiftsType() || isEmployeeType()) && (
          <Box paddingBottom="s8">
            <Text size="200" weight="medium" color="grey1000">
              {i18n.t('project_management.modals.export_projects_data.input_label.select_employee')}
            </Text>

            <EmployeeSelector
              alwaysOpen
              includeInactive
              multiple
              onSelect={handleEmployeeChange}
              value={selectedEmployees}
            />
          </Box>
        )}
        <Select
          label={i18n.t('project_management.modals.export_projects_data.input_label.date_range')}
          options={dateRangeOptions()}
          field={exportForm.get('date_range')}
        />
        {date_range === 'custom_range' && (
          <Box flexDirection="row" gap="s16">
            <DayPicker
              mandatory
              label={i18n.t('project_management.modals.export_projects_data.input_label.from')}
              field={exportForm.get('from_date')}
              maxDate={fromMaxDate()}
            />
            <DayPicker
              mandatory
              label={i18n.t('project_management.modals.export_projects_data.input_label.to')}
              field={exportForm.get('to_date')}
              minDate={moment(stringToDate(exportForm.get('from_date').value, 'ui')).startOf('day')}
            />
          </Box>
        )}
        {isTotalTimeType() && (
          <Checkbox
            checked={includeEmployeesWithoutTimeTracked}
            label={i18n.t(
              'project_management.modals.export_projects_data.input_label.include_employees_without_time_tracked'
            )}
            size="small"
            onChange={(event) => onIncludeEmployeesWithoutTimeTrackedChange(event.target.checked)}
          />
        )}
      </Box>
    </Modal>
  )
}

export default observer(ExportProjectsModal)
